'use client'

import { useState, useEffect } from 'react'
import { Eye, EyeOff, X, Loader2 } from 'lucide-react'
import { createClient, isSupabaseConfigured } from '@/lib/supabase/client'
import { Z } from '@/lib/z-index'
import { useFreemium } from '@/lib/freemium-context'
import { signInWithGooglePopup } from '@/lib/oauth-popup'
import { toast } from 'sonner'

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
)

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 8,
  border: '1.5px solid #E5E7EB',
  fontSize: 14,
  color: '#111827',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color .15s',
}

interface AuthModalProps {
  open: boolean
  defaultTab?: 'login' | 'register'
  onClose: () => void
  onSuccess: () => void
  redirectAfterSuccess?: string
}

export default function AuthModal({ open, defaultTab = 'register', onClose, onSuccess, redirectAfterSuccess }: AuthModalProps) {
  const [tab, setTab] = useState<'login' | 'register'>(defaultTab)
  const [loading, setLoading] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const { refreshLimits } = useFreemium()

  useEffect(() => {
    if (open) {
      setTab(defaultTab)
      setForm({ name: '', email: '', password: '' })
      setShowPw(false)
    }
  }, [open, defaultTab])

  if (!open) return null

  const supabase = isSupabaseConfigured ? createClient() : null

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }))

  const handleGoogleSignIn = async () => {
    if (!supabase) { toast.error('Authentification non configurée.'); return }
    setLoading(true)
    // Connexion via popup : préserve la page (fichier uploadé + reprise auto)
    // quand aucune redirection n'est demandée. Repli sur redirection si bloqué.
    await signInWithGooglePopup({
      supabase,
      onAuthenticated: () => {
        onSuccess()
        if (redirectAfterSuccess) {
          // Redirection explicitement voulue (ex. /pricing) → on y va.
          window.location.href = redirectAfterSuccess
        } else {
          // Reste en place : re-lit l'état d'auth pour déclencher la reprise.
          void refreshLimits()
          onClose()
        }
      },
      onCancel: () => setLoading(false),
      onError: (msg) => { toast.error('Erreur Google : ' + msg); setLoading(false) },
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!supabase) {
      toast.error('Authentification non configurée.')
      return
    }
    setLoading(true)
    try {
      if (tab === 'register') {
        const { data, error } = await supabase.auth.signUp({
          email: form.email,
          password: form.password,
          options: { data: { full_name: form.name } },
        })
        if (error) throw error
        // `data.session` présent → confirmation email désactivée : l'utilisateur
        // est déjà connecté (SIGNED_IN va déclencher la reprise du traitement).
        // Absent → confirmation requise : on l'invite à vérifier son email.
        if (data.session) {
          toast.success('Compte créé ! Vous êtes connecté.')
        } else {
          toast.success('Compte créé ! Vérifiez votre email pour confirmer.', { duration: 6000 })
        }
        onSuccess()
        onClose()
        if (redirectAfterSuccess) window.location.href = redirectAfterSuccess
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password,
        })
        if (error) throw error
        toast.success('Connexion réussie !')
        onSuccess()
        onClose()
        if (redirectAfterSuccess) window.location.href = redirectAfterSuccess
      }
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Erreur inconnue'
      const fr = raw.includes('Invalid login') ? 'Email ou mot de passe incorrect.'
        : raw.includes('already registered') ? 'Cet email est déjà utilisé.'
        : raw.includes('Password should') ? 'Le mot de passe doit contenir au moins 6 caractères.'
        : raw
      toast.error(fr)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: Z.modal, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', width: '100%', maxWidth: 420, position: 'relative' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <X size={20} color="#9CA3AF" />
        </button>

        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <img src="/qualyse-logo.png" alt="Qualyse" style={{ width: 32, height: 32, borderRadius: 8 }} />
          <span style={{ fontSize: 16, fontWeight: 800, color: '#111827', fontFamily: 'Georgia, serif' }}>Qualyse</span>
        </div>

        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>
          {tab === 'register' ? 'Créez votre compte gratuit' : 'Bon retour sur Qualyse 👋'}
        </h2>
        {tab === 'register' && (
          <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 24px' }}>Accédez à tous les modules</p>
        )}
        {tab === 'login' && <div style={{ height: 20 }} />}

        {/* Google OAuth */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '11px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 14, fontWeight: 600, color: '#374151', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, marginBottom: 16 }}
        >
          <GoogleIcon />
          Continuer avec Google
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: '#F3F4F6' }} />
          <span style={{ fontSize: 12, color: '#6B7280' }}>ou avec email</span>
          <div style={{ flex: 1, height: 1, background: '#F3F4F6' }} />
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tab === 'register' && (
            <input
              type="text"
              placeholder="Prénom"
              value={form.name}
              onChange={set('name')}
              required
              style={INPUT}
              onFocus={e => { e.target.style.borderColor = '#1B4332' }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB' }}
            />
          )}

          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={set('email')}
            required
            style={INPUT}
            onFocus={e => { e.target.style.borderColor = '#1B4332' }}
            onBlur={e => { e.target.style.borderColor = '#E5E7EB' }}
          />

          <div style={{ position: 'relative' }}>
            <input
              type={showPw ? 'text' : 'password'}
              placeholder="Mot de passe"
              value={form.password}
              onChange={set('password')}
              required
              style={{ ...INPUT, paddingRight: 44 }}
              onFocus={e => { e.target.style.borderColor = '#1B4332' }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB' }}
            />
            <button
              type="button"
              onClick={() => setShowPw(v => !v)}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', display: 'flex' }}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {tab === 'login' && (
            <button
              type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#1B4332', textAlign: 'right', padding: 0, alignSelf: 'flex-end' }}
            >
              Mot de passe oublié ?
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{ marginTop: 4, padding: '12px 0', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#fff', background: loading ? '#9CA3AF' : '#1B4332', border: 'none', cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Chargement...' : tab === 'register' ? 'Créer mon compte gratuit' : 'Se connecter'}
          </button>
        </form>

        <div style={{ marginTop: 16 }}>
          <div style={{ textAlign: 'center', fontSize: 13, color: '#6B7280' }}>
            {tab === 'register' ? (
              <>Déjà inscrit ?{' '}
                <button
                  type="button"
                  onClick={() => setTab('login')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1B4332', fontWeight: 600, fontSize: 13, padding: 0, textDecoration: 'underline' }}
                >
                  Se connecter
                </button>
              </>
            ) : (
              <>Pas encore de compte ?{' '}
                <button
                  type="button"
                  onClick={() => setTab('register')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1B4332', fontWeight: 600, fontSize: 13, padding: 0, textDecoration: 'underline' }}
                >
                  S&apos;inscrire
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
